import React, { VoidFunctionComponent } from "react";
import dynamic from "next/dynamic";
import { Title } from "@/components/responsive/product/title/Title";
import { Rating } from "@/components/responsive/product/rating/Rating";
import { Questions } from "@/components/responsive/product/questions/Questions";
import { PriceBlock } from "@/components/responsive/product/priceBlock/PriceBlock";
import { Seller } from "@/components/responsive/product/seller/Seller";
import { TagsBlock } from "@/components/responsive/product/tagsBlock/TagsBlock";
import { RefundOffersBlock } from "@/components/responsive/product/refundOffersBlock/RefundOffersBlock";
import { Warranties } from "@/components/responsive/product/warranties/Warranties";
import { FlashSalePicto } from "@/components/responsive/product/PictoBloc/FlashSalePicto";
import { PlaceholderBlock } from "@/components/responsive/product/placeholderBlock/PlaceholderBlock";
import { InformationSheetLink } from "@/components/responsive/product/informationSheetLink/InformationSheetLink";
import { FreeShippingPicto } from "@/components/responsive/product/PictoBloc/FreeShippingPicto";
import { OfferTypeLabel } from "@/components/responsive/product/offerTypeLabel/OfferTypeLabel";
import { Product } from "@/models/scope/productSheet/Product";
import { ProductDiscountDetails } from "@/models/scope/productSheet/ProductDiscountDetails";
import { Offers } from "@/models/scope/productSheet/offer/Offers";
import { OfferItem } from "@/models/scope/productSheet/offer/OfferItem";
import {
    REVIEWS_DISPLAY_THRESHOLD,
    SOLD_BY_MKP, SOLD_BY_BUT,
    LAYER_TYPE,
    INFORMATION_SHEET_LINK,
    PLACEHOLDER_TYPE
} from "@/utils/constants";
import Utils from "@/libs/Utils";


const DynamicCreditBlock = dynamic(
    () => import('@/components/responsive/product/creditBlock/CreditBlock'),
    { ssr: false }
);

interface Props {
    productData: Product,
    winningOffer: OfferItem | null,
    offersData: Offers | null;
    setPriceIncludeVAT: (includeVAT: boolean) => void;
    priceIncludeVAT: boolean;
    offerList: Offers | null;
    setLayerType: (layerType: string) => void;
    setShowCloseLayerButton: (status: boolean) => void;
    state: string | undefined;
    setWarrantySelected: (status: boolean) => void;
    isSelectedWarranty: boolean;
    priceInfos: {
        price: number;
        priceWithoutTax: number;
        crossedOutPrice: number;
        crossedOutPriceWithoutTax: number;
        discountDetails: ProductDiscountDetails | null;
        priceEco: number;
    } | null;
    sponsorProductWinningOffer: OfferItem[];
}

export const ProductMainInfos: VoidFunctionComponent<Props> = ({
    productData,
    winningOffer,
    setPriceIncludeVAT,
    priceIncludeVAT,
    priceInfos,
    offerList,
    state,
    offersData,
    sponsorProductWinningOffer,
    setLayerType,
    setShowCloseLayerButton,
    isSelectedWarranty,
    setWarrantySelected,
}) => {
    const {
        productReviewsList, reviewQuestions, nbrNote, soldBy, isPei, markLabel, title,
        frontLabel, type, commercialOperations, financingOffers, cetelemInfos, price,
        privateSalePrice, eligibilityToPrivateSale, warranties, id } = productData;
    const isEligibleTo4xCb = (financingOffers?.length > 0 && financingOffers[0].butEligibility == 1 && sponsorProductWinningOffer?.length == 0)
    let eligibleSoldBy = [SOLD_BY_BUT, SOLD_BY_MKP];
    let maxDisplayedPicto = Utils.getMaxDisplayedCommercialOperationsOfTypeCommercial(commercialOperations);
    let stickersNumbers = commercialOperations?.filter((operation) => operation.type === "Commercial" && !operation.isFlash).length;
    maxDisplayedPicto = maxDisplayedPicto - commercialOperations?.filter((operation) => operation.isFlash).length;

    return <>
        <FlashSalePicto commercialOperations={commercialOperations} eligibilityToPrivateSale={productData.eligibilityToPrivateSale} soldBy={soldBy} eligibleSoldBy={eligibleSoldBy} />
        <div style={{ marginBottom: '20px' }}>
            <TagsBlock
                commercialOperations={productData.commercialOperations.filter(
                    (operation) => operation.type === "Commercial" && !operation.isFlash
                ).filter((item, index) => ((maxDisplayedPicto > index) && (index < stickersNumbers)))}
                isColumnDirection={false}
                eligibilityToPrivateSale={!winningOffer && productData.eligibilityToPrivateSale} soldBy={soldBy}
                eligibleSoldBy={eligibleSoldBy} stickersNumbers={stickersNumbers} maxDisplayedPicto={maxDisplayedPicto} offersData={offersData} />
            {(soldBy !== SOLD_BY_MKP) && <FreeShippingPicto commercialOperations={commercialOperations} />}
        </div>
        <Title frontLabel={frontLabel} title={title} markLabel={markLabel} type={type} />
        <div className="rating-question block-layout">
            {(soldBy !== SOLD_BY_MKP ? nbrNote >= REVIEWS_DISPLAY_THRESHOLD : nbrNote > 0) &&
                <Rating reviewListStatistics={productReviewsList.reviewListStatistics} setLayerType={setLayerType} underline={true} />}
            <Questions questionCount={reviewQuestions.summary.questionsCount} setLayerType={setLayerType} />
        </div>
        {offerList ? <OfferTypeLabel
            soldBy={soldBy}
            newOffersCount={offerList.newOffersCount}
            reconditionedOffersCount={offerList.reconditionedOffersCount}
            cheapestPriceWithoutBestNewOffer={offerList.cheapestPriceWithoutBestNewOffer}
            cheapestPriceWithoutBestNewOfferWithoutTax={offerList.cheapestPriceWithoutBestNewOfferWithoutTax}
            cheapestPriceWithoutBestReconditionedOffer={offerList.cheapestPriceWithoutBestReconditionedOffer}
            cheapestPriceWithoutBestReconditionedOfferWithoutTax={offerList.cheapestPriceWithoutBestReconditionedOfferWithoutTax}
            state={state}
            priceIncludeVAT={priceIncludeVAT}
            handleClick={setLayerType}
        /> : soldBy !== SOLD_BY_BUT ? <PlaceholderBlock
            type={soldBy === SOLD_BY_MKP ? PLACEHOLDER_TYPE.PICKUP : PLACEHOLDER_TYPE.GENERIC}
        /> : <></>}
        <PriceBlock
            showWithoutTaxOptin={productData.showWithoutTaxOptin}
            eligibilityToPrivateSale={!winningOffer && productData.eligibilityToPrivateSale}
            privateSalePrice={productData.privateSalePrice}
            endDateRI={productData.endDateRI}
            privateSalePriceWithoutTax={productData.privateSalePriceWithoutTax}
            privateDiscountDetails={productData.privateDiscountDetails}
            soldBy={soldBy}
            datePrixBarreMkp={soldBy === SOLD_BY_MKP ? offerList?.datePrixBarreMkp ?? true : true}
            priceInfos={priceInfos}
            priceIncludeVAT={priceIncludeVAT}
            setPriceIncludeVAT={setPriceIncludeVAT}
            id={`main-price-${productData.id}`}
        />
        <InformationSheetLink
            url={productData.documentList && productData.documentList[INFORMATION_SHEET_LINK]}
        />
        <Seller
            sellerType={soldBy}
            isWebOnly={isPei}
            sellerMKP={winningOffer?.seller}
            setLayerType={setLayerType}
            isLayer={true}
            setShowCloseLayerButton={setShowCloseLayerButton}
            isWinningOffer={true}
        />
        {isEligibleTo4xCb && <DynamicCreditBlock
            financingOffers={financingOffers}
            soldBy={soldBy}
            setLayerType={setLayerType}
            cetelemInfos={cetelemInfos}
            price={price}
            privateSalePrice={privateSalePrice}
            eligibilityToPrivateSale={eligibilityToPrivateSale}
        />}
        <Warranties
            warranties={warranties.extendedWarranties}
            onTooltipClick={() => {
                setLayerType(LAYER_TYPE.WARRANTIES);
            }}
            id={'productSheetWarranties'}
            handleSelectWarranty={setWarrantySelected}
            isSelectedWarranty={isSelectedWarranty}
        />
        <RefundOffersBlock
            refundOfferList={productData.refundOfferList}
            promotionalOffers={productData.promotionalOffers}
            setLayerType={() => { setLayerType(LAYER_TYPE.BUNDLE) }}
        />
    </>
}